import { nameByPlatform } from "~/constants/platfroms";
import type { IOutcomeConsensus } from "~/types/event";
import type { Platform } from "~/types/platform";
import type { IRowChangeDisplay } from "~/utils/getRowChangeDisplay";
import { isFiniteNumber } from "~/utils/isFiniteNumber";

import { labelBySpreadBand } from "./marketQualityCopy";
import type { ISpreadCell, ISpreadRange } from "./marketQualityTypes";
import { formatSpread, formatSpreadRange, getSpreadQuality } from "./panelCopy";
import { getSpreadBand, isSpreadRange, toneBySpreadBand } from "./spreadBand";

interface IOutcomeSpreadCellParams {
  outcome: IOutcomeConsensus | undefined;
  averageSpread: number | null;
  rulesOn: boolean;
}

export const getOutcomeSpreadRange = (
  outcome: IOutcomeConsensus | null | undefined
): ISpreadRange | null =>
  isFiniteNumber(outcome?.spreadMin) && isFiniteNumber(outcome?.spreadMax)
    ? { min: outcome.spreadMin, max: outcome.spreadMax }
    : null;

const getRangeCaption = (
  range: ISpreadRange,
  tightest: Platform | null
): IRowChangeDisplay | null => {
  const band = getSpreadBand(range.min);
  const tone = toneBySpreadBand[band];

  if (!isSpreadRange(range)) {
    return { label: labelBySpreadBand[band], tone };
  }

  return tightest ? { label: nameByPlatform[tightest], tone } : null;
};

const getRangeCell = (outcome: IOutcomeConsensus | undefined): ISpreadCell => {
  const range = getOutcomeSpreadRange(outcome);

  return {
    value: formatSpreadRange(range),
    caption: range
      ? getRangeCaption(range, outcome?.tightestSpreadPlatform ?? null)
      : null,
  };
};

const getAverageCell = (averageSpread: number | null): ISpreadCell => ({
  value: formatSpread(averageSpread),
  caption: getSpreadQuality(averageSpread),
});

export const getOutcomeSpreadCell = ({
  outcome,
  averageSpread,
  rulesOn,
}: IOutcomeSpreadCellParams): ISpreadCell =>
  rulesOn ? getRangeCell(outcome) : getAverageCell(averageSpread);
